import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Star, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReviewSection = ({ vendorId }) => {
  const { isAuthenticated, token } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');

  useEffect(() => {
    fetchReviews();
  }, [vendorId]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/vendors/${vendorId}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rating) {
      toast.error('Please select a rating');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/vendors/${vendorId}/reviews`,
        { vendor_id: vendorId, rating, comment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Review submitted successfully!');
      setRating(0);
      setComment('');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (value, interactive = false) => {
    return [...Array(5)].map((_, i) => (
      <button
        key={i}
        type={interactive ? 'button' : undefined}
        disabled={!interactive}
        className={interactive ? 'cursor-pointer' : ''}
        onClick={() => interactive && setRating(i + 1)}
        onMouseEnter={() => interactive && setHoverRating(i + 1)}
        onMouseLeave={() => interactive && setHoverRating(0)}
      >
        <Star
          className={`h-5 w-5 transition-colors duration-150 ${
            (interactive ? (hoverRating || rating) : value) > i
              ? 'fill-[#D4AF37] text-[#D4AF37]'
              : 'text-stone-300'
          }`}
        />
      </button>
    ));
  };

  return (
    <div className="space-y-8">
      <h2 className="heading-lg">Reviews</h2>

      {/* Review Form */}
      {isAuthenticated ? (
        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-sm border border-stone-100 card-shadow space-y-4">
          <div className="space-y-2">
            <Label>Your Rating</Label>
            <div className="flex items-center gap-1" data-testid="rating-stars">
              {renderStars(rating, true)}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="review-comment">Your Review</Label>
            <Textarea
              id="review-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your experience with this vendor..."
              rows={4}
              required
              className="input-wedding resize-none"
              data-testid="review-comment"
            />
          </div>

          <Button
            type="submit"
            className="btn-primary btn-lift"
            disabled={submitting}
            data-testid="review-submit"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Submit Review
          </Button>
        </form>
      ) : (
        <div className="bg-stone-50 p-6 rounded-sm text-center">
          <p className="text-stone-600 mb-4">Please login to leave a review</p>
          <Button asChild className="btn-primary" data-testid="review-login-button">
            <a href="/client/auth">Login to Leave a Review</a>
          </Button>
        </div>
      )}

      {/* Reviews List */}
      <div className="space-y-6">
        {loading ? (
          <div className="text-center py-8">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-stone-400" />
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-8 text-stone-500">
            No reviews yet. Be the first to review!
          </div>
        ) : (
          reviews.map((review) => (
            <article
              key={review.id}
              className="bg-white p-6 rounded-sm border border-stone-100 card-shadow"
              data-testid={`review-${review.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-medium text-[#1C1917]">{review.user_name}</h4>
                  <p className="text-sm text-stone-500">
                    {format(new Date(review.created_at), 'MMMM d, yyyy')}
                  </p>
                </div>
                <div className="flex items-center gap-0.5">
                  {renderStars(review.rating)}
                </div>
              </div>
              <p className="text-stone-600 leading-relaxed">{review.comment}</p>
            </article>
          ))
        )}
      </div>
    </div>
  );
};

export default ReviewSection;
